import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  User as UserIcon,
  Shield,
  BookOpen,
  CreditCard,
  History,
  Tag,
  Bell,
  Settings,
  HelpCircle,
  FileText,
  LogOut,
  Globe,
  ChevronRight,
  CheckCircle2,
  Lock,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Button,
  Select,
  Modal,
  useToast,
} from '../ui';

export interface StudentSubscription {
  id: number;
  plan_name: string;
  plan_code: string;
  sector_id: number;
  sector_name: string;
  status: string;
  end_at: string;
}

export const AccountView: React.FC = () => {
  const {
    currentUser,
    sectors,
    subjects,
    questionLanguage,
    setQuestionLanguage,
    logoutUser,
    setProfileModalOpen,
    setSelectedSectorId,
    selectedSectorId,
  } = useApp();
  const { showToast } = useToast();

  const [activeSubTab, setActiveSubTab] = useState<
    'profile' | 'exam' | 'subscriptions' | 'settings' | 'support'
  >('profile');

  const [mySubscriptions, setMySubscriptions] = useState<StudentSubscription[]>([]);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState<any | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  const fetchMySubscriptions = async () => {
    try {
      const res = await fetch('/api/subscriptions.php?action=my_subscriptions');
      const data = await res.json();
      if (data.success) {
        setMySubscriptions(data.subscriptions || []);
      }
    } catch (e) {
      console.error('Error fetching student subscriptions:', e);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch('/api/plans.php');
      const data = await res.json();
      if (data.success) {
        setAvailablePlans(data.plans || []);
      }
    } catch (e) {
      console.error('Error fetching plans:', e);
    }
  };

  useEffect(() => {
    fetchMySubscriptions();
    fetchPlans();
  }, []);

  const handleStartCheckout = async (plan: any) => {
    setSelectedPlanForCheckout(plan);
    setIsProcessingCheckout(true);

    try {
      // 1. Create Razorpay Test Order on Backend (Authoritative Price Validation)
      const res = await fetch('/api/payments/razorpay/create_order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: plan.id }),
      });
      const orderData = await res.json();

      if (!orderData.success) {
        showToast(orderData.error || 'Failed to initialize payment order', 'error');
        setIsProcessingCheckout(false);
        return;
      }

      // 2. Perform Test Verification Simulation with Sandbox Key
      const testPaymentId = 'pay_test_' + Math.random().toString(36).substring(2, 10);
      const testSignature = orderData.order_id + '_valid_sig'; // Mock verification token

      const verifyRes = await fetch('/api/payments/razorpay/verify_payment.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: plan.id,
          razorpay_order_id: orderData.order_id,
          razorpay_payment_id: testPaymentId,
          razorpay_signature: testSignature,
        }),
      });
      const verifyData = await verifyRes.json();

      if (verifyData.success) {
        showToast(`Subscribed to ${plan.name}! Sandbox Payment Verified.`, 'success', 'Subscription Active');
        setIsCheckoutModalOpen(false);
        fetchMySubscriptions();
      } else {
        showToast(verifyData.error || 'Payment verification failed', 'error');
      }
    } catch (e) {
      showToast('Error during payment processing', 'error');
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const mySector = sectors.find((s) => s.id === (selectedSectorId || currentUser?.target_sector_id));
  const mySubjects = subjects.filter((subj) => (mySector ? subj.sector_id === mySector.id : true));

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      {/* Header Profile Summary Hero */}
      <Card variant="default" className="bg-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-2 sm:p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#E8FAF9] text-[#1b8e88] font-black text-xl flex items-center justify-center border border-[#56E8E1]/50 overflow-hidden shrink-0 shadow-xs">
              {currentUser?.profile_picture ? (
                <img src={currentUser.profile_picture} alt={currentUser.name} className="w-full h-full object-cover" />
              ) : currentUser?.name ? (
                currentUser.name.charAt(0).toUpperCase()
              ) : (
                <UserIcon className="w-8 h-8 text-[#1fb4ac]" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-extrabold text-[#111111]">
                  {currentUser?.name || 'Student Account'}
                </h1>
                <Badge variant={currentUser?.role === 'admin' ? 'warning' : 'primary'} size="sm">
                  {currentUser?.role || 'Student'}
                </Badge>
              </div>
              <p className="text-xs text-[#5F6368] mt-0.5">{currentUser?.mobile || 'No mobile linked'}</p>
              {mySector && (
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-[11px] font-semibold text-[#5F6368]">Target Exam:</span>
                  <span className="text-xs font-bold text-[#1b8e88] bg-[#E8FAF9] px-2 py-0.5 rounded-md border border-[#56E8E1]/40">
                    {mySector.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={() => setProfileModalOpen(true)}>
              Edit Profile
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                logoutUser();
                showToast('Signed out successfully', 'info');
              }}
              leftIcon={<LogOut className="w-3.5 h-3.5" />}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </Card>

      {/* Account Navigation Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left Sidebar Menu */}
        <div className="space-y-1">
          {[
            { id: 'profile', label: 'Profile & Target Exam', icon: <UserIcon className="w-4 h-4" /> },
            { id: 'exam', label: 'My Subjects & Exam', icon: <BookOpen className="w-4 h-4" /> },
            { id: 'subscriptions', label: 'Subscriptions & Access', icon: <CreditCard className="w-4 h-4" /> },
            { id: 'settings', label: 'Preferences & Language', icon: <Settings className="w-4 h-4" /> },
            { id: 'support', label: 'Help & About REVise', icon: <HelpCircle className="w-4 h-4" /> },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSubTab(item.id as any)}
              className={`w-full text-left px-4 py-3 text-xs font-bold rounded-2xl transition-all flex items-center justify-between cursor-pointer ${
                activeSubTab === item.id
                  ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                  : 'bg-white text-[#5F6368] hover:text-[#111111] hover:bg-[#F7FAFA] border border-[#E5E9E9]'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className={activeSubTab === item.id ? 'text-[#1fb4ac]' : 'text-[#80868B]'}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#80868B]" />
            </button>
          ))}
        </div>

        {/* Right Content View */}
        <div className="md:col-span-3 space-y-6">
          {activeSubTab === 'profile' && (
            <Card variant="default">
              <CardHeader>
                <CardTitle>Personal Details</CardTitle>
                <CardDescription>Manage your account profile information and target competitive exam.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="p-3.5 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9] space-y-1">
                    <span className="text-[#5F6368] font-semibold">Full Name</span>
                    <p className="font-bold text-[#111111]">{currentUser?.name || 'Not specified'}</p>
                  </div>
                  <div className="p-3.5 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9] space-y-1">
                    <span className="text-[#5F6368] font-semibold">Mobile Number</span>
                    <p className="font-bold text-[#111111]">{currentUser?.mobile || 'Not specified'}</p>
                  </div>
                  <div className="p-3.5 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9] space-y-1">
                    <span className="text-[#5F6368] font-semibold">Email Address</span>
                    <p className="font-bold text-[#111111]">{currentUser?.email || 'Not specified'}</p>
                  </div>
                  <div className="p-3.5 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9] space-y-1">
                    <span className="text-[#5F6368] font-semibold">City / Location</span>
                    <p className="font-bold text-[#111111]">{currentUser?.city || 'Not specified'}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#E5E9E9]">
                  <h4 className="text-xs font-bold text-[#111111] uppercase tracking-wider mb-2">Target Exam Sector</h4>
                  <Select
                    value={selectedSectorId || currentUser?.target_sector_id || ''}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setSelectedSectorId(val || null);
                      showToast('Target exam sector updated', 'success');
                    }}
                    options={[
                      { label: 'Select Target Exam Sector...', value: '' },
                      ...sectors.map((s) => ({ label: `${s.name} (${s.code})`, value: s.id })),
                    ]}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {activeSubTab === 'exam' && (
            <Card variant="default">
              <CardHeader>
                <CardTitle>My Subjects</CardTitle>
                <CardDescription>Subjects included in your current active exam target sector.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {mySubjects.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mySubjects.map((subj) => (
                      <div key={subj.id} className="p-3.5 bg-white rounded-2xl border border-[#E5E9E9] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-[#E8FAF9] text-[#1fb4ac] font-bold text-xs flex items-center justify-center">
                            {subj.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-[#111111]">{subj.name}</p>
                            <p className="text-[10px] text-[#5F6368]">{subj.code}</p>
                          </div>
                        </div>
                        <Badge variant="primary" size="sm">Active</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#5F6368]">No subjects found for current selection.</p>
                )}
              </CardContent>
            </Card>
          )}

          {activeSubTab === 'subscriptions' && (
            <Card variant="default">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>My Subscriptions</CardTitle>
                    <CardDescription>Category-specific active exam subscriptions.</CardDescription>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => setIsCheckoutModalOpen(true)} leftIcon={<Zap className="w-3.5 h-3.5" />}>
                    Upgrade / Buy Plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {mySubscriptions.length > 0 ? (
                  <div className="space-y-3">
                    {mySubscriptions.map((sub) => (
                      <div key={sub.id} className="p-4 bg-[#E8FAF9] rounded-2xl border border-[#56E8E1]/50 flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="primary" dot>{sub.sector_name}</Badge>
                            <Badge variant="success" size="sm">{sub.status}</Badge>
                          </div>
                          <h4 className="text-sm font-bold text-[#111111]">{sub.plan_name}</h4>
                          <p className="text-xs text-[#5F6368]">Valid until {new Date(sub.end_at).toLocaleDateString()}</p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-[#20A66A]" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9] text-center space-y-2">
                    <p className="text-xs font-bold text-[#111111]">No Active Category Subscriptions</p>
                    <p className="text-xs text-[#5F6368]">Subscribe to an exam sector plan to unlock premium test series and practice modules.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeSubTab === 'settings' && (
            <Card variant="default">
              <CardHeader>
                <CardTitle>Language & System Preferences</CardTitle>
                <CardDescription>Configure application language and interface preferences.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3.5 bg-[#F7FAFA] rounded-2xl border border-[#E5E9E9]">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#1fb4ac]" />
                    <div>
                      <p className="text-xs font-bold text-[#111111]">Interface & Question Language</p>
                      <p className="text-[10px] text-[#5F6368]">Bilingual support for English and Hindi</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const nextLang = questionLanguage === 'en' ? 'hi' : 'en';
                      setQuestionLanguage(nextLang);
                      showToast(`Language switched to ${nextLang === 'en' ? 'English' : 'Hindi'}`, 'info');
                    }}
                  >
                    {questionLanguage === 'en' ? 'English (EN)' : 'Hindi (HI)'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSubTab === 'support' && (
            <Card variant="default">
              <CardHeader>
                <CardTitle>About REVise Platform</CardTitle>
                <CardDescription>Exam Revision & Practice SaaS Platform v2.0</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs text-[#5F6368] leading-relaxed">
                <p>
                  REVise is an enterprise-grade competitive exam preparation platform providing instant feedback practice sets, timed test simulations, rapid flashcard revisions, and detailed accuracy analytics.
                </p>
                <div className="flex flex-wrap items-center gap-4 pt-2 font-bold text-[#111111]">
                  <span>Terms of Service</span>
                  <span>•</span>
                  <span>Privacy Policy</span>
                  <span>•</span>
                  <span>Help Center</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modal: Student Plan Checkout (Sandbox Simulation) */}
      <Modal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        title="Select Category Subscription Plan"
        description="Choose a subscription plan to unlock complete exam series access."
      >
        <div className="space-y-3">
          {availablePlans.map((plan) => (
            <div key={plan.id} className="p-4 bg-white rounded-2xl border border-[#E5E9E9] hover:border-[#1fb4ac] transition-all flex items-center justify-between">
              <div className="space-y-1">
                <Badge variant="primary" size="sm">{plan.sector_name}</Badge>
                <h4 className="text-sm font-bold text-[#111111]">{plan.name}</h4>
                <p className="text-xs font-black text-[#1b8e88]">₹{plan.price} <span className="text-[10px] font-medium text-[#5F6368]">/ {plan.duration_days} days</span></p>
              </div>
              <Button
                variant="primary"
                size="sm"
                isLoading={isProcessingCheckout && selectedPlanForCheckout?.id === plan.id}
                onClick={() => handleStartCheckout(plan)}
              >
                Checkout
              </Button>
            </div>
          ))}
          {availablePlans.length === 0 && (
            <p className="text-xs text-[#5F6368] text-center py-6">No subscription plans available for purchase currently.</p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AccountView;
